// src/dashboard/pages/teacher/Categories/utils/categoriesDashboardUtils.js

import {
  buildCategoriesForSeason,
  getAgeAtEndOfSeason,
  getAgeCategoryKey,
  getCurrentSeasonStartYear,
  getWeightBracket,
  isMorpho,
  seasonStringFromStartYear,
  weightCategories,
} from "./judoCategories";

const API_BASE = process.env.REACT_APP_API_URL || "";

export function isExternalUrl(url) {
  try {
    const parsed = new URL(url);
    return parsed.protocol === "http:" || parsed.protocol === "https:";
  } catch {
    return false;
  }
}

export function toAbsoluteUrl(url) {
  if (!url) return "";

  const value = String(url).trim();

  if (!value) return "";
  if (isExternalUrl(value)) return value;
  if (value.startsWith("data:") || value.startsWith("blob:")) return value;
  if (!value.startsWith("/uploads")) return value;

  return `${API_BASE.replace(/\/$/, "")}${value}`;
}

export function normalizeString(value) {
  return String(value || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim();
}

export function getSeasonOptions() {
  const start = getCurrentSeasonStartYear();

  return [start - 1, start, start + 1, start + 2].map(
    seasonStringFromStartYear
  );
}

export function getYearOptions() {
  const currentYear = new Date().getFullYear();

  return Array.from({ length: 110 }, (_, index) =>
    String(currentYear - index)
  );
}

export function getGroupOptions(adherents = []) {
  const groups = new Set();

  adherents.forEach((adherent) => {
    (adherent?.Groups || []).forEach((group) => {
      if (group?.nom) groups.add(group.nom);
    });
  });

  return [...groups].sort((a, b) =>
    a.localeCompare(b, "fr", { sensitivity: "base" })
  );
}

export function getBirthYear(dateStr) {
  if (!dateStr) return null;

  const [year] = String(dateStr).split("T")[0].split("-");

  return Number(year) || null;
}

export function formatDateFr(dateStr) {
  if (!dateStr) return "—";

  const [year, month, day] = String(dateStr).split("T")[0].split("-");

  if (!year || !month || !day) return "—";

  return `${day}/${month}/${year}`;
}

export function getMemberDisplayName(member) {
  const nom = String(member?.nom || "").trim();
  const prenom = String(member?.prenom || "").trim();

  const fullName = `${nom.toUpperCase()} ${prenom}`.trim();

  return fullName || "Adhérent non renseigné";
}

export function getMemberPhoto(member) {
  return (
    toAbsoluteUrl(member?.photo_url || member?.photoURL || member?.avatar) ||
    "/img/user_avatar.jpg"
  );
}

export function getGroupsLabel(member) {
  return (member?.Groups || [])
    .map((group) => group?.nom)
    .filter(Boolean)
    .join(", ");
}

export function getAgeCategoryOptions(season, selectedGenre = "Tous") {
  return buildCategoriesForSeason(season)
    .filter((row) => selectedGenre === "Tous" || row.genre === selectedGenre)
    .map((row) => ({
      value: row.ageCategory,
      label:
        selectedGenre === "Tous"
          ? `${row.label} — ${row.genre}`
          : row.label,
    }));
}

export function isInGroup(adherent, groupName) {
  if (!groupName) return true;

  return (adherent?.Groups || []).some((group) => group?.nom === groupName);
}

export function isValidGender(genre) {
  return genre === "Masculin" || genre === "Féminin";
}

export function hasValidWeight(adherent) {
  const poids = Number(adherent?.poids);
  return Number.isFinite(poids) && poids > 0;
}

function buildBaseMember(adherent, season, categoriesMap) {
  const poids = Number(adherent?.poids);
  const validWeight = Number.isFinite(poids) && poids > 0;
  const validGender = isValidGender(adherent?.genre);
  const validBirthDate = !!adherent?.date_naissance;

  let age = null;
  let ageCategory = null;
  let ageLabel = "";
  let ageYears = "";
  let weightBracket = null;

  if (validBirthDate) {
    age = getAgeAtEndOfSeason(adherent.date_naissance, season);
  }

  if (validGender && age !== null && !Number.isNaN(age)) {
    ageCategory = getAgeCategoryKey(age, adherent.genre);
  }

  if (ageCategory) {
    const categoryMeta = categoriesMap[ageCategory];
    ageLabel = categoryMeta?.label || ageCategory;
    ageYears = categoryMeta?.annees || "";
  }

  if (ageCategory && validWeight) {
    weightBracket = getWeightBracket(ageCategory, poids);
  }

  return {
    ...adherent,
    poids: validWeight ? poids : null,
    hasWeight: validWeight,
    age,
    ageCategory,
    ageLabel,
    ageYears,
    weightBracket,
    birthYear: getBirthYear(adherent.date_naissance),
    groupsLabel: getGroupsLabel(adherent),
    isCategorized: !!(ageCategory && weightBracket && validWeight),
    missingReason: {
      weight: !validWeight,
      birthDate: !validBirthDate,
      gender: !validGender,
      ageCategory: !ageCategory,
      weightBracket: !!ageCategory && validWeight && !weightBracket,
    },
  };
}

export function computeProcessedMembers(adherents = [], season, group = "") {
  const categories = buildCategoriesForSeason(season);
  const categoriesMap = {};

  categories.forEach((category) => {
    categoriesMap[category.ageCategory] = category;
  });

  const processedMembers = [];
  const excludedMembers = [];
  const allDisplayMembers = [];

  adherents.forEach((adherent) => {
    const seasonOk = adherent?.saison_active === season;
    const groupOk = isInGroup(adherent, group);

    if (!seasonOk || !groupOk) return;

    const member = buildBaseMember(adherent, season, categoriesMap);

    allDisplayMembers.push(member);

    if (member.isCategorized) {
      processedMembers.push(member);
    } else {
      excludedMembers.push(member);
    }
  });

  return {
    processedMembers,
    excludedMembers,
    allDisplayMembers,
  };
}

export function filterMembers(members = [], filters = {}) {
  const query = normalizeString(filters.query);
  const genre = filters.genre || "Tous";
  const year = filters.year || "Tous";
  const ageCategory = filters.ageCategory || "Tous";
  const weightStatus = filters.weightStatus || "with";

  return members.filter((member) => {
    const searchable = normalizeString(
      [
        member?.nom,
        member?.prenom,
        member?.genre,
        member?.ageLabel,
        member?.weightBracket,
        member?.groupsLabel,
      ]
        .filter(Boolean)
        .join(" ")
    );

    const matchesQuery = !query || searchable.includes(query);
    const matchesGenre = genre === "Tous" || member.genre === genre;
    const matchesYear = year === "Tous" || String(member.birthYear) === year;
    const matchesAge =
      ageCategory === "Tous" || member.ageCategory === ageCategory;

    const matchesWeight =
      weightStatus === "all" ||
      (weightStatus === "with" && member.hasWeight) ||
      (weightStatus === "without" && !member.hasWeight);

    return (
      matchesQuery &&
      matchesGenre &&
      matchesYear &&
      matchesAge &&
      matchesWeight
    );
  });
}

export function sortMembers(members = [], sortBy = "name-asc") {
  return [...members].sort((a, b) => {
    const nameA = `${a.nom || ""} ${a.prenom || ""}`.trim();
    const nameB = `${b.nom || ""} ${b.prenom || ""}`.trim();

    if (sortBy === "name-desc") {
      return nameB.localeCompare(nameA, "fr", { sensitivity: "base" });
    }

    if (sortBy === "weight-asc") {
      return (
        Number(a.poids || 0) - Number(b.poids || 0) ||
        nameA.localeCompare(nameB, "fr", { sensitivity: "base" })
      );
    }

    if (sortBy === "weight-desc") {
      return (
        Number(b.poids || 0) - Number(a.poids || 0) ||
        nameA.localeCompare(nameB, "fr", { sensitivity: "base" })
      );
    }

    if (sortBy === "category-asc") {
      return (
        String(a.ageLabel || "").localeCompare(String(b.ageLabel || ""), "fr", {
          sensitivity: "base",
        }) || nameA.localeCompare(nameB, "fr", { sensitivity: "base" })
      );
    }

    return nameA.localeCompare(nameB, "fr", { sensitivity: "base" });
  });
}

export function getBracketKey(member) {
  return `${member.ageCategory}|${member.genre}|${member.weightBracket}`;
}

export function buildCountsByBracket(members = []) {
  const counts = {};
  const membersByBracket = {};

  members
    .filter((member) => member.isCategorized)
    .forEach((member) => {
      const key = getBracketKey(member);

      counts[key] = (counts[key] || 0) + 1;
      membersByBracket[key] = membersByBracket[key] || [];
      membersByBracket[key].push(member);
    });

  return {
    counts,
    membersByBracket,
  };
}

function parseWeightLabel(label) {
  const normalized = String(label).replace(",", ".");
  const match = normalized.match(/\d+(?:\.\d+)?/);
  return match ? parseFloat(match[0]) : null;
}

export function getBracketsForRow(row, counts = {}) {
  const list = weightCategories[row.ageCategory] || [];

  if (!list.includes("Groupes Morphologiques")) {
    return list;
  }

  const prefix = `${row.ageCategory}|${row.genre}|`;

  const dynamic = Object.keys(counts)
    .filter((key) => key.startsWith(prefix))
    .map((key) => key.slice(prefix.length));

  return dynamic.sort((a, b) => {
    const weightA = parseWeightLabel(a);
    const weightB = parseWeightLabel(b);

    if (weightA !== null && weightB !== null) return weightA - weightB;

    return String(a).localeCompare(String(b), "fr", { sensitivity: "base" });
  });
}

function rowMatchesYear(row, selectedYear) {
  if (!selectedYear || selectedYear === "Tous") return true;

  const year = Number(selectedYear);

  if (String(row.annees || "").includes(" - ")) {
    const [start, end] = row.annees
      .split(" - ")
      .map((item) => Number(String(item).trim()));

    return year >= start && year <= end;
  }

  if (String(row.annees || "").includes("et avant")) {
    const fromYear = Number(String(row.annees).match(/\d+/)?.[0]);
    return year <= fromYear;
  }

  return true;
}

export function buildCategoryRowsForDisplay({
  season,
  genre,
  year,
  ageCategory,
  onlyFilledCategories,
  counts,
}) {
  return buildCategoriesForSeason(season)
    .filter((row) => {
      const matchesGenre = genre === "Tous" || row.genre === genre;
      const matchesAge =
        ageCategory === "Tous" || row.ageCategory === ageCategory;
      const matchesYear = rowMatchesYear(row, year);

      if (!matchesGenre || !matchesAge || !matchesYear) return false;

      if (!onlyFilledCategories) return true;

      const brackets = getBracketsForRow(row, counts);

      return brackets.some((bracket) => {
        const key = `${row.ageCategory}|${row.genre}|${bracket}`;
        return (counts[key] || 0) > 0;
      });
    })
    .map((row) => ({
      ...row,
      isMorphological: isMorpho(row.ageCategory),
      brackets: getBracketsForRow(row, counts),
    }));
}

export function computeCategoryStats({
  adherents = [],
  processedMembers = [],
  filteredMembers = [],
  excludedMembers = [],
  counts = {},
}) {
  const filledCategories = new Set();
  const filledBrackets = new Set();

  filteredMembers
    .filter((member) => member.isCategorized)
    .forEach((member) => {
      filledCategories.add(`${member.ageCategory}|${member.genre}`);
      filledBrackets.add(getBracketKey(member));
    });

  const missingWeightCount = excludedMembers.filter(
    (member) => member.missingReason?.weight
  ).length;

  return {
    totalAdherents: adherents.length,
    eligibleMembers: processedMembers.length,
    visibleMembers: filteredMembers.length,
    excludedMembers: excludedMembers.length,
    missingWeightCount,
    filledCategories: filledCategories.size,
    filledBrackets: filledBrackets.size,
    bracketCount: Object.keys(counts).length,
  };
}